import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';

import type { AgentManagedExtensionPluginState } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtension, isAgentManagedExtension } from '../../common/utils';

export type AgentManagedExtensionMeta =
	| { type: 'OPEN_CONFIG' }
	| { type: 'CLOSE_CONFIG' }
	| { type: 'TOGGLE_CONFIG' };

const META_KEY = 'agentManagedExtension';

export const setMeta = (tr: Transaction, meta: AgentManagedExtensionMeta): Transaction =>
	tr.setMeta(META_KEY, meta);

export const getMeta = (
	tr: Transaction | ReadonlyTransaction,
): AgentManagedExtensionMeta | undefined =>
	tr.getMeta(META_KEY) as AgentManagedExtensionMeta | undefined;

export const initialState = (): AgentManagedExtensionPluginState => ({
	isConfigOpen: false,
});

const collectLocalIds = (doc: EditorState['doc']): Set<string> => {
	const found = new Set<string>();
	doc.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}
		const localId = node.attrs.localId as string | undefined;
		if (localId) {
			found.add(localId);
		}
		return false;
	});
	return found;
};

const findNewLocalIds = (oldState: EditorState, newState: EditorState): string[] => {
	const oldLocalIds = collectLocalIds(oldState.doc);
	const newLocalIds = collectLocalIds(newState.doc);
	return [...newLocalIds].filter((localId) => !oldLocalIds.has(localId));
};

const isLocalUserInsertTransaction = (tr: Transaction | ReadonlyTransaction): boolean =>
	tr.docChanged &&
	!tr.getMeta('isRemote') &&
	!tr.getMeta('replaceDocument') &&
	// Editor transactions do not carry a single "local user insert" bit. For
	// auto-open we treat history-tracked document changes as user-authored and
	// exclude remote/document-replacement transactions above.
	tr.getMeta('addToHistory') !== false;

const getSelectedAgentManagedLocalId = (state: EditorState): string | undefined =>
	findAgentManagedExtension(state)?.node.attrs.localId as string | undefined;

/**
 * Pure reducer for AgentManagedExtensionMeta actions.
 */
export const applyMeta = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMeta | undefined,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!meta) {
		return prev;
	}
	switch (meta.type) {
		case 'OPEN_CONFIG': {
			const configLocalId = getSelectedAgentManagedLocalId(newState);
			if (!configLocalId) {
				return { isConfigOpen: false };
			}
			return { configLocalId, isConfigOpen: true };
		}
		case 'CLOSE_CONFIG':
			return { isConfigOpen: false };
		case 'TOGGLE_CONFIG': {
			if (prev.isConfigOpen) {
				return { isConfigOpen: false };
			}
			const configLocalId = getSelectedAgentManagedLocalId(newState);
			if (!configLocalId) {
				return { isConfigOpen: false };
			}
			return { configLocalId, isConfigOpen: true };
		}
		default:
			return prev;
	}
};

/**
 * Marks a locally inserted agent-managed block for config auto-open.
 * Existing content loaded through document replacement/collab transactions
 * should never auto-open config.
 */
export const applyAutoOpenOnInsert = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	oldState: EditorState,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!isLocalUserInsertTransaction(tr)) {
		return prev;
	}

	const newLocalIds = findNewLocalIds(oldState, newState);
	if (newLocalIds.length !== 1) {
		return prev;
	}

	return {
		...prev,
		pendingAutoOpenLocalId: newLocalIds[0],
	};
};

/**
 * Returns `{ isConfigOpen: false }` when the popup is open but the
 * selection has moved away from the configured block. Otherwise returns
 * `prev` unchanged.
 */
export const applyAutoCloseOnSelectionChange = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!prev.isConfigOpen || !tr.selectionSet) {
		return prev;
	}
	const selectedLocalId = getSelectedAgentManagedLocalId(newState);
	if (selectedLocalId && selectedLocalId === prev.configLocalId) {
		return prev;
	}
	return { isConfigOpen: false };
};
